import { EventRule } from '@tebuto/api/clients/models/EventRule'
import { BookedEvent } from '@tebuto/api/therapists/models/BookedEvent'
import { Booking } from '@tebuto/api/therapists/models/Booking'
import { CalendarSeminarSessionResponse } from '@tebuto/api/therapists/models/CalendarSeminarSessionResponse'
import { City } from '@tebuto/api/therapists/models/City'
import { EventCategory } from '@tebuto/api/therapists/models/EventCategory'
import { Meeting } from '@tebuto/api/therapists/models/Meeting'
import { OnlineMeeting } from '@tebuto/api/therapists/models/OnlineMeeting'
import { Opening } from '@tebuto/api/therapists/models/Opening'
import { TebutoRuleEvent } from '@tebuto/api/therapists/models/TebutoRuleEvent'
import { TebutoStandaloneEvent } from '@tebuto/api/therapists/models/TebutoStandaloneEvent'
import { ThirdPartyEvent } from '@tebuto/api/therapists/models/ThirdPartyEvent'
import { useEventCategoryMapping } from 'app/app/(main)/termine/hooks'
import { BusinessHoursInput } from 'fullcalendar'
import { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import { useCallback } from 'react'
import { isMoveException } from './event-rule-exception'
import { useComponentTranslation } from './translation.components'

export interface FullCalendarEvent<T = any> {
    title: string
    start: Date
    end: Date
    allDay: boolean
    extendedProps: T
}

export type EventWithType =
    | ParsedTebutoRuleEvent
    | ParsedTebutoStandaloneEvent
    | ParsedThirdPartyEvent
    | ParsedMeetingEvent
    | ParsedSeminarEvent
export type FullCalendarEventWithType = FullCalendarEvent<EventWithType>

export type ScheduleEventType = ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent | ParsedThirdPartyEvent
export type FullCalendarScheduleEvent = FullCalendarEvent<ScheduleEventType>

interface BaseEvent {
    title: string
    start: DateTime<true>
    end: DateTime<true>
    color: string
    isAllDay: boolean
    isHighlighted?: boolean
}

export interface ParsedTebutoRuleEvent extends BaseEvent {
    type: 'rule'
    isSkipped: boolean
    isCurrentlyInBooking: boolean
    isException: boolean
    hasCustomTitle: boolean
    rule: EventRule
    category: EventCategory
    /** When the rule links multiple categories, UI can show a segmented accent using these colors (same order as `rule.categoryIds`). */
    flexibleCategoryColors?: string[]
    /** Resolved category names for flexible rules (`rule.categoryIds.length > 1`), same order as `rule.categoryIds` (omits unknown names). */
    flexibleCategoryNames?: string[]
    details?: BookedEvent
}

export interface ParsedTebutoStandaloneEvent extends BaseEvent {
    type: 'standalone'
    id: number
    details: BookedEvent
    rule?: EventRule
    category?: EventCategory
}

export interface ParsedThirdPartyEvent extends BaseEvent {
    type: 'third-party'
    id: string
    description?: string
    calendarId: string
    isBlocking: boolean
    provider: ThirdPartyEvent.provider
}

export const MEETING_EVENT_COLOR = '#009087'
export const SEMINAR_EVENT_COLOR = '#B45309'

export interface ParsedMeetingEvent extends BaseEvent {
    type: 'meeting'
    slug: string
    meeting: Meeting
}

export interface ParsedSeminarEvent extends BaseEvent {
    type: 'seminar'
    id: number
    occurrenceId: number
    seminarId: number
    seminarTitle: string
    label: string | null
}

/** Flexible rule slot (multiple categories) where a concrete booking/category selection should drive the calendar title (pipe-joined names when unbooked; selected category when booked). */
export function isFlexibleRuleSlotShowingSelectedCategory(details: BookedEvent | undefined, rule: EventRule): boolean {
    if (rule.categoryIds.length <= 1) {
        return false
    }
    if (!details) {
        return false
    }
    if (details.booking) {
        return true
    }
    switch (details.status) {
        case BookedEvent.status.BOOKED:
        case BookedEvent.status.APPROVED:
        case BookedEvent.status.ASSIGNED:
        case BookedEvent.status.MOVE_REQUESTED:
            return true
        default:
            return false
    }
}

export function convertRuleEventToFullCalendarEvent(
    event: TebutoRuleEvent,
    categories: Record<number, EventCategory>,
    rules: EventRule[],
    highlightedEventStart?: DateTime<true>,
    options?: { flexibleSlotCalendarTitle?: string }
): FullCalendarEvent<ParsedTebutoRuleEvent> | null {
    const category = categories[event.categoryId]
    const rule = rules.find((rule) => rule.id === event.ruleId)
    if (!rule) {
        return null
    }

    const isFlexibleRule = rule.categoryIds.length > 1
    const flexibleCategoryColors = isFlexibleRule
        ? rule.categoryIds.map((id) => categories[id]?.color ?? '#9CA3AF')
        : undefined
    const flexibleCategoryNames = isFlexibleRule
        ? rule.categoryIds.map((id) => categories[id]?.name?.trim()).filter((name): name is string => Boolean(name))
        : undefined

    const showBookedCategoryTitle = isFlexibleRuleSlotShowingSelectedCategory(event.details, rule)

    const unbookedFlexibleTitle =
        flexibleCategoryNames && flexibleCategoryNames.length > 0
            ? flexibleCategoryNames.join(' | ')
            : options?.flexibleSlotCalendarTitle

    const calendarTitle =
        isFlexibleRule && !showBookedCategoryTitle
            ? (unbookedFlexibleTitle ?? event.title)
            : showBookedCategoryTitle
              ? (category?.name ?? event.title)
              : event.title

    const start = DateTime.fromISO(event.start)
    const end = DateTime.fromISO(event.end)

    return {
        title: calendarTitle,
        start: start.toJSDate(),
        end: end.toJSDate(),
        allDay: false,
        backgroundColor: category.color,
        durationEditable: false,
        editable: end > DateTime.now(),
        extendedProps: {
            type: 'rule',
            start,
            end,
            rule,
            category,
            flexibleCategoryColors,
            flexibleCategoryNames,
            isAllDay: false,
            title: calendarTitle,
            color: category.color,
            details: event.details,
            isSkipped: event.isSkipped,
            isCurrentlyInBooking: event.isCurrentlyInBooking,
            isException: event.isException,
            hasCustomTitle: event.hasCustomTitle,
            isHighlighted: highlightedEventStart && start.equals(highlightedEventStart)
        }
    } as FullCalendarEvent<ParsedTebutoRuleEvent>
}

export function convertStandaloneEventToFullCalendarEvent(
    event: TebutoStandaloneEvent,
    categories: Record<number, EventCategory>,
    rules: EventRule[] = []
): FullCalendarEvent<ParsedTebutoStandaloneEvent> {
    const category = event.categoryId ? categories[event.categoryId] : undefined
    const rule = event.ruleId != null ? rules.find((candidate) => candidate.id === event.ruleId) : undefined
    const start = DateTime.fromISO(event.start)
    const end = DateTime.fromISO(event.end)
    const color = category ? category.color : '#009087'
    return {
        title: event.title,
        start: start.toJSDate(),
        end: end.toJSDate(),
        allDay: false,
        backgroundColor: color,
        editable: end > DateTime.now(),
        extendedProps: {
            type: 'standalone',
            id: event.id,
            title: event.title,
            start,
            end,
            color,
            isAllDay: false,
            details: event.details,
            category,
            rule
        }
    } as FullCalendarEvent<ParsedTebutoStandaloneEvent>
}

export function getScheduleEventRule(
    event: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent
): EventRule | undefined {
    if (event.type === 'rule') {
        return event.rule
    }
    return event.rule
}

export function convertThirdPartyEventToFullCalendarEvent(
    event: ThirdPartyEvent
): FullCalendarEvent<ParsedThirdPartyEvent> {
    const start = DateTime.fromISO(event.start)
    const end = DateTime.fromISO(event.end)
    return {
        title: event.title,
        start: start.toJSDate(),
        end: end.toJSDate(),
        allDay: event.isAllDay,
        backgroundColor: event.color,
        editable: false,
        show: 'background',
        extendedProps: {
            type: 'third-party',
            id: event.id,
            title: event.title,
            start,
            end,
            color: event.color,
            isAllDay: event.isAllDay,
            isBlocking: event.isBlocking,
            description: event.description,
            calendarId: event.calendarId,
            provider: event.provider
        }
    } as FullCalendarEvent<ParsedThirdPartyEvent>
}

export function convertMeetingToFullCalendarEvent(meeting: Meeting): FullCalendarEvent<ParsedMeetingEvent> {
    const start = DateTime.fromISO(meeting.start)
    const end = DateTime.fromISO(meeting.end)
    return {
        title: meeting.title,
        start: start.toJSDate(),
        end: end.toJSDate(),
        allDay: false,
        backgroundColor: MEETING_EVENT_COLOR,
        editable: end > DateTime.now(),
        durationEditable: false,
        extendedProps: {
            type: 'meeting',
            slug: meeting.slug,
            title: meeting.title,
            start,
            end,
            color: MEETING_EVENT_COLOR,
            isAllDay: false,
            meeting
        }
    } as FullCalendarEvent<ParsedMeetingEvent>
}

export function convertSeminarSessionToFullCalendarEvent(
    session: CalendarSeminarSessionResponse
): FullCalendarEvent<ParsedSeminarEvent> {
    const start = DateTime.fromISO(session.start)
    const end = DateTime.fromISO(session.end)
    const title = session.label ? `${session.seminarTitle} — ${session.label}` : session.seminarTitle
    return {
        title,
        start: start.toJSDate(),
        end: end.toJSDate(),
        allDay: false,
        backgroundColor: SEMINAR_EVENT_COLOR,
        editable: false,
        durationEditable: false,
        extendedProps: {
            type: 'seminar',
            id: session.id,
            occurrenceId: session.occurrenceId,
            seminarId: session.seminarId,
            seminarTitle: session.seminarTitle,
            label: session.label,
            title,
            start,
            end,
            color: SEMINAR_EVENT_COLOR,
            isAllDay: false
        }
    } as FullCalendarEvent<ParsedSeminarEvent>
}

export function getMinimumStartTime(openings: Opening[], extraHour = false): string {
    let minimumStartTime: DateTime | null = null

    for (const opening of openings) {
        const startTime = DateTime.fromISO(`1970-01-01T${opening.startTime}`)
        if (!minimumStartTime || startTime < minimumStartTime) {
            minimumStartTime = startTime
        }
    }

    if (minimumStartTime) {
        if (extraHour) {
            minimumStartTime = minimumStartTime.minus({ minutes: 30 })
        }
        return minimumStartTime.toFormat('HH:mm')
    }

    return '06:00'
}

export function getMaximumEndTime(openings: Opening[], extraHour = false): string {
    let maximumEndTime: DateTime | null = null

    for (const opening of openings) {
        const endTime = DateTime.fromISO(`1970-01-01T${opening.endTime}`)
        if (!maximumEndTime || endTime > maximumEndTime) {
            maximumEndTime = endTime
        }
    }

    if (maximumEndTime) {
        if (extraHour && !(maximumEndTime.hour === 23 && maximumEndTime.minute === 30)) {
            maximumEndTime = maximumEndTime.plus({ minutes: 30 })
        }
        return maximumEndTime.toFormat('HH:mm')
    }

    return '23:59'
}

export function getBusinessHours(openings: Opening[]): BusinessHoursInput {
    return [0, 1, 2, 3, 4, 5, 6]
        .map((day) => openings.filter((opening) => getDayNumber(opening.day) === day))
        .map((openingsForDay, index) => {
            if (openingsForDay.length > 0) {
                return {
                    daysOfWeek: [index],
                    startTime: getMinimumStartTime(openingsForDay),
                    endTime: getMaximumEndTime(openingsForDay)
                }
            }
        })
        .filter((opening) => opening)
}

export function getDayNumber(day: Opening.day): number {
    switch (day) {
        case Opening.day.MONDAY:
            return 1
        case Opening.day.TUESDAY:
            return 2
        case Opening.day.WEDNESDAY:
            return 3
        case Opening.day.THURSDAY:
            return 4
        case Opening.day.FRIDAY:
            return 5
        case Opening.day.SATURDAY:
            return 6
        case Opening.day.SUNDAY:
            return 0
    }
}

export function getDayByNumber(day: number): Opening.day {
    switch (day) {
        case 1:
            return Opening.day.MONDAY
        case 2:
            return Opening.day.TUESDAY
        case 3:
            return Opening.day.WEDNESDAY
        case 4:
            return Opening.day.THURSDAY
        case 5:
            return Opening.day.FRIDAY
        case 6:
            return Opening.day.SATURDAY
        case 0:
            return Opening.day.SUNDAY
        default:
            throw new Error('Invalid day number')
    }
}

export function createDayTitleGetter(t: TFunction<string[], undefined>) {
    return (day: Opening.day): string => {
        switch (day) {
            case Opening.day.MONDAY:
                return t('monday')
            case Opening.day.TUESDAY:
                return t('tuesday')
            case Opening.day.WEDNESDAY:
                return t('wednesday')
            case Opening.day.THURSDAY:
                return t('thursday')
            case Opening.day.FRIDAY:
                return t('friday')
            case Opening.day.SATURDAY:
                return t('saturday')
            case Opening.day.SUNDAY:
                return t('sunday')
        }
    }
}

export function useGetDayTitleByStart() {
    const { t } = useComponentTranslation()
    return (date: DateTime<true>): string => {
        switch (date.weekday) {
            case 1:
                return t('monday')
            case 2:
                return t('tuesday')
            case 3:
                return t('wednesday')
            case 4:
                return t('thursday')
            case 5:
                return t('friday')
            case 6:
                return t('saturday')
            case 7:
                return t('sunday')
        }
    }
}

export function useGetMeetingProviderLabel() {
    const { t } = useComponentTranslation()
    return (provider?: OnlineMeeting.provider) => {
        switch (provider) {
            case OnlineMeeting.provider.ZOOM:
                return t('zoom')
            case OnlineMeeting.provider.MICROSOFT_TEAMS:
                return t('microsoftTeams')
            case OnlineMeeting.provider.TEBUTO_MEET:
                return t('tebutoMeet')
            default:
                return t('unknown')
        }
    }
}

export function useGetFederalStateName() {
    const { t } = useComponentTranslation()
    return (federalStateCode: City.federalStateCode): string => {
        switch (federalStateCode) {
            case City.federalStateCode.BW:
                return t('postalCodes.bw')
            case City.federalStateCode.BY:
                return t('postalCodes.by')
            case City.federalStateCode.BE:
                return t('postalCodes.be')
            case City.federalStateCode.BB:
                return t('postalCodes.bb')
            case City.federalStateCode.HB:
                return t('postalCodes.hb')
            case City.federalStateCode.HH:
                return t('postalCodes.hh')
            case City.federalStateCode.HE:
                return t('postalCodes.he')
            case City.federalStateCode.MV:
                return t('postalCodes.mv')
            case City.federalStateCode.NI:
                return t('postalCodes.ni')
            case City.federalStateCode.NW:
                return t('postalCodes.nw')
            case City.federalStateCode.RP:
                return t('postalCodes.rp')
            case City.federalStateCode.SL:
                return t('postalCodes.sl')
            case City.federalStateCode.SN:
                return t('postalCodes.sn')
            case City.federalStateCode.ST:
                return t('postalCodes.st')
            case City.federalStateCode.SH:
                return t('postalCodes.sh')
            case City.federalStateCode.TH:
                return t('postalCodes.th')
        }
    }
}

export function showRule(event: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent): boolean {
    return !!event.rule && !event.rule.deleted && event.rule.interval > 0
}

export function getEarliestStartDateInFuture(rule: EventRule): DateTime<true> {
    const ruleStart = DateTime.fromISO(rule.start) as DateTime<true>

    if (rule.interval === 0 || ruleStart >= DateTime.now()) {
        return ruleStart.startOf('day')
    }

    let start = ruleStart
    while (start < DateTime.now()) {
        start = start.plus({ weeks: rule.interval })
    }

    return start.startOf('day')
}

/**
 * Generates a URL to view a calendar in the browser based on the provider and event ID
 * @param provider The calendar provider (Google or Microsoft)
 * @param eventId The external ID of the event
 * @returns The URL to view the calendar in the browser
 */
export function getCalendarViewUrl(event: ParsedThirdPartyEvent): string {
    switch (event.provider) {
        case ThirdPartyEvent.provider.GOOGLE:
            return `https://calendar.google.com/calendar/event?eid=${btoa(`${event.id} ${event.calendarId}`).replace(
                '==',
                ''
            )}`
        case ThirdPartyEvent.provider.MICROSOFT:
            return `https://outlook.office365.com/owa/?itemid=${event.id}&exvsurl=1&path=/calendar/item`
        default:
            throw new Error(`Unsupported calendar provider: ${event.provider}`)
    }
}

type ScheduleSlideOverCarrier = Pick<FullCalendarScheduleEvent, 'extendedProps'>

export interface FindMatchingScheduleParsedEventOptions {
    targetStart?: DateTime<true>
}

function isThirdPartyScheduleEvent(
    candidate: ScheduleSlideOverCarrier['extendedProps']
): candidate is ParsedThirdPartyEvent {
    return candidate.type === 'third-party'
}

function ruleOccurrenceMatchesStartEnd(
    candidate: ParsedTebutoRuleEvent,
    start: DateTime<true>,
    end: DateTime<true>
): boolean {
    return candidate.start.equals(start) && candidate.end.equals(end)
}

function ruleHasMoveExceptionForOriginDate(rule: EventRule, originIsoDate: string): boolean {
    return (rule.exceptions ?? []).some(
        (exception) => isMoveException(exception) && DateTime.fromISO(exception.date).toISODate() === originIsoDate
    )
}

export function findMatchingScheduleParsedEvent(
    scheduleEvents: ReadonlyArray<ScheduleSlideOverCarrier>,
    selection: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent,
    options?: FindMatchingScheduleParsedEventOptions
): ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent | undefined {
    const bookingId = selection.details?.booking?.id

    if (bookingId) {
        for (const scheduleEvent of scheduleEvents) {
            const candidate = scheduleEvent.extendedProps
            if (isThirdPartyScheduleEvent(candidate)) {
                continue
            }

            if (candidate.details?.booking?.id === bookingId) {
                return candidate as ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent
            }
        }
    }

    if (options?.targetStart && selection.type === 'rule') {
        const duration = selection.end.diff(selection.start)
        const targetEnd = options.targetStart.plus(duration) as DateTime<true>
        for (const scheduleEvent of scheduleEvents) {
            const candidate = scheduleEvent.extendedProps
            if (candidate.type === 'rule' && candidate.rule.id === selection.rule.id) {
                if (ruleOccurrenceMatchesStartEnd(candidate, options.targetStart, targetEnd)) {
                    return candidate
                }
            }
        }
    }

    for (const scheduleEvent of scheduleEvents) {
        const candidate = scheduleEvent.extendedProps
        if (isThirdPartyScheduleEvent(candidate)) {
            continue
        }

        if (selection.type === 'standalone' && candidate.type === 'standalone') {
            if (candidate.id === selection.id) {
                return candidate
            }
        }

        if (selection.type === 'rule' && candidate.type === 'rule') {
            if (
                candidate.rule.id === selection.rule.id &&
                ruleOccurrenceMatchesStartEnd(candidate, selection.start, selection.end)
            ) {
                return candidate
            }
        }
    }

    if (selection.type === 'rule' && selection.details?.id) {
        for (const scheduleEvent of scheduleEvents) {
            const candidate = scheduleEvent.extendedProps
            if (candidate.type === 'rule' && candidate.details?.id === selection.details.id) {
                return candidate
            }
        }
    }

    if (selection.type === 'rule') {
        const originIsoDate = selection.start.toISODate()
        if (originIsoDate) {
            for (const scheduleEvent of scheduleEvents) {
                const candidate = scheduleEvent.extendedProps
                if (
                    candidate.type === 'rule' &&
                    candidate.rule.id === selection.rule.id &&
                    ruleHasMoveExceptionForOriginDate(candidate.rule, originIsoDate)
                ) {
                    return candidate
                }
            }
        }
    }

    return undefined
}

export function summarizeSlideOverParsedSelection(event: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent): string {
    const d = event.details
    const booking = d?.booking
    const key =
        event.type === 'standalone'
            ? { type: 'standalone' as const, id: event.id }
            : { type: 'rule' as const, ruleId: event.rule.id, start: event.start.toISO(), end: event.end.toISO() }

    const exceptionIds =
        event.type === 'rule'
            ? [...(event.rule.exceptions ?? [])].map((exception) => exception.id).sort((a, b) => a - b)
            : []

    return JSON.stringify({
        key,
        status: d?.status ?? null,
        detailsId: d?.id ?? null,
        bookingId: booking?.id ?? null,
        outageFeeApplies: booking?.outageFeeApplies ?? null,
        bookingClientId: booking?.client?.id ?? null,
        bookingIsConfirmed: booking?.isConfirmed ?? null,
        paymentId: booking?.payment?.id ?? null,
        bookingIsOutage: booking?.isOutage ?? null,
        bookingPaymentPaidAt: booking?.payment?.paidAt ?? null,
        exceptionIds,
        ruleStart: event.type === 'rule' ? event.rule.start : null,
        ruleEndDate: event.type === 'rule' ? event.rule.endDate : null,
        ruleInterval: event.type === 'rule' ? event.rule.interval : null,
        isException: event.type === 'rule' ? event.isException : null,
        isSkipped: event.type === 'rule' ? event.isSkipped : null
    })
}

export function slideOverParsedSelectionSnapshotsEqual(
    a: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent,
    b: ParsedTebutoRuleEvent | ParsedTebutoStandaloneEvent
): boolean {
    return summarizeSlideOverParsedSelection(a) === summarizeSlideOverParsedSelection(b)
}

export function useParseSelectedEvent() {
    const { eventCategoryMapping } = useEventCategoryMapping()
    return useCallback(
        (booking: Booking) => {
            // Use the booking's embedded category as fallback for deleted categories (legacy data)
            const categoryMappingWithFallback = {
                ...eventCategoryMapping,
                [booking.event.category.id]: eventCategoryMapping[booking.event.category.id] ?? booking.event.category
            }
            return convertStandaloneEventToFullCalendarEvent(
                {
                    start: booking.event.start,
                    end: booking.event.end,
                    id: booking.event.id,
                    details: { ...booking.event, booking, notice: null, log: [] },
                    title: booking.event.customTitle || booking.event.category.name,
                    color: booking.event.category.color,
                    isAllDay: false,
                    categoryId: booking.event.category.id
                },
                categoryMappingWithFallback
            ).extendedProps
        },
        [eventCategoryMapping]
    )
}
